import React from 'react'
import {
  InteriorQuality,
  RealEstateCategory,
  Salutation,
  SegmentationType,
  TransactionType,
  YesNoOrByAppointment,
} from '../../../../../@types/types'
import { Flow, FlowName, FlowScreenElement } from '../../../../../@types/flow-types'
import { AGENT_GARAGE_RENT_FLOW_PATH } from '../../../../../services/routes/routes'
import {
  SCREEN_ADDRESS,
  SCREEN_API_SEARCH_DATA,
  SCREEN_BASIC_INFO,
  SCREEN_CONTACT_INFO,
  SCREEN_COSTS,
  SCREEN_DESCRIPTION,
  SCREEN_MULTIMEDIA,
  SCREEN_OBJECT_NUMBERS,
  SCREEN_OTHER_FACILITIES,
  SCREENS,
} from '../../../../../services/domain/wizardScreens'
import Address from '../../../../../components/pages/insertionPage/screens/address/Address'
import Multimedia from '../../../../../components/pages/insertionPage/screens/multimediaNew/Multimedia'

import Description from '../../../../../components/pages/insertionPage/screens/description/Description'
import ContactInfo from '../../../../../components/pages/insertionPage/screens/contactInfo/ContactInfo'
import CostsGarageRent from '../../../../../components/pages/insertionPage/screens/costs/wrappers/garageRent/CostsGarageRent'
import { agentGarageRentSchema } from './agentGarageRentSchema'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import {
  heightGarageField,
  lengthGarageField,
  usableAreaField,
  widthGarageField,
} from '../../../../../components/pages/insertionPage/screens/otherFacilities/wrapper/otherFacilitiesFieldsWrapper'
import OtherFacilities from '../../../../../components/pages/insertionPage/screens/otherFacilities/OtherFacilities'
import { UsableAreaVariants } from '../../../../../components/pages/insertionPage/screens/otherFacilities/fields/usableAreaField/UsableAreaField'
import { getDefaultClasses } from '../../../../../utils/style-utils'
import BasicInfo from '../../../../../components/pages/insertionPage/screens/basicInfo/BasicInfo'
import {
  buildingType,
  constructionConditionField,
  constructionYearField,
  lastModernizationField,
} from '../../../../../components/pages/insertionPage/screens/basicInfo/wrappers/basicInfoFieldsWrapper'
import { BuildingTypeOption } from '../../../../../@types/dropdown-types'
import { courtageInformationToApi } from '../../../../../api/realEstate/converters/toApi/estate/courtageInformation/courtageInformationToApi'
import { ObjectNumbersScreen } from '../../../../../components/pages/insertionPage/screens/objectNumber/ObjectNumbersScreen'
import ApiSearchData from '../../../../../components/pages/insertionPage/screens/apiSearchData/ApiSearchData'
import { apiSearchDataToApi } from '../../../../../api/realEstate/converters/toApi/estate/apiSearchData/apiSearchDataToApi'

const agentGarageRentFlowScreens: FlowScreenElement[] = [
  {
    screen: SCREENS[SCREEN_ADDRESS],
    element: (props) => <Address {...props} />,
  },
  { screen: SCREENS[SCREEN_OBJECT_NUMBERS], element: (props) => <ObjectNumbersScreen {...props} /> },
  {
    screen: SCREENS[SCREEN_BASIC_INFO],
    element: (props) => (
      <BasicInfo
        {...props}
        screenTooltipKey='tooltip.screens.basicInfo.garageRent'
        properties={[
          buildingType(
            [
              BuildingTypeOption.NO_INFORMATION,
              BuildingTypeOption.GARAGE,
              BuildingTypeOption.UNDERGROUND_GARAGE,
              BuildingTypeOption.CAR_PARK,
              BuildingTypeOption.CARPORT,
              BuildingTypeOption.STREET_PARKING,
              BuildingTypeOption.DUPLEX,
            ],
            getDefaultClasses('leftOnDesk')
          ),
          constructionConditionField(),
          constructionYearField(),
          lastModernizationField(),
        ]}
      />
    ),
  },
  {
    screen: {
      ...SCREENS[SCREEN_COSTS],
      translation: 'wizard.navigation.label.costs2',
      defaultTranslation: 'Prices & Costs',
    },
    element: (props) => <CostsGarageRent {...props} screenTooltipKey='tooltip.screens.costs.allRent' />,
  },
  {
    screen: SCREENS[SCREEN_DESCRIPTION],
    element: (props) => <Description {...props} hasFurnishingNote hasLocationNote hasOtherNote />,
  },
  {
    screen: SCREENS[SCREEN_MULTIMEDIA],
    element: (props) => (
      <Multimedia
        {...props}
        doCloseMultimediaStatusMessage={props.doCloseMultimediaStatusMessage!}
        allowedTypesToDisplay={[
          MultimediaUploaderType.PICTURE,
          MultimediaUploaderType.FLOOR_PLAN,
          MultimediaUploaderType.DOCUMENT,
          MultimediaUploaderType.VIDEO,
        ]}
        allowMultimediaLinks
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_OTHER_FACILITIES],
    element: (props) => (
      <OtherFacilities
        {...props}
        screenTooltipKey='tooltip.screens.otherFacilities.allRent'
        properties={[
          lengthGarageField(),
          usableAreaField(UsableAreaVariants.OTHER, getDefaultClasses('right')),
          widthGarageField(),
          heightGarageField(),
        ]}
      />
    ),
  },
  {
    screen: SCREENS[SCREEN_API_SEARCH_DATA],
    element: (props) => <ApiSearchData {...props} />,
  },
  {
    screen: SCREENS[SCREEN_CONTACT_INFO],
    element: (props) => <ContactInfo {...props} hasContactManager />,
  },
]

export const agentGarageRentFlow: Flow = {
  flowInformation: {
    owner: 'wizard.navigation.header.agent',
    estateType: 'wizard.navigation.header.garage',
    offerType: 'wizard.navigation.header.rents',
    path: AGENT_GARAGE_RENT_FLOW_PATH,
    name: FlowName.AGENT_GARAGE_RENT,
    validationSchema: agentGarageRentSchema,
    metaData: {
      category: RealEstateCategory.GARAGE,
      segmentation: SegmentationType.AGENT,
      transaction: TransactionType.RENT,
    },
    courtageToApiMapper: courtageInformationToApi,
    apiSearchDataToApiMapper: apiSearchDataToApi,
  },
  defaultStateValues: {
    hideStreetAndStreetNumber: false,
    estateFeatures: {},
    interiorQuality: InteriorQuality.NO_INFORMATION,
    lanCables: YesNoOrByAppointment.NO_INFORMATION,
    airConditioning: YesNoOrByAppointment.NO_INFORMATION,
    salutation: Salutation.NO_SALUTATION,
  },
  flowScreens: agentGarageRentFlowScreens,
}
